import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell, ComposedChart, Area,
} from 'recharts';
import { useTheme } from '../contexts/ThemeContext';

// ── Paleta SIDEB ──
const COLORS = {
  green: '#13983D',
  navy: '#2E4974',
  blue: '#0974BA',
  yellow: '#F9B047',
  red: '#E83A3C',
  teal: '#13983D',
  purple: '#2E4974',
  orange: '#F9B047',
  pink: '#E83A3C',
};
const PALETTE = [COLORS.blue, COLORS.teal, COLORS.yellow, COLORS.red, COLORS.purple, COLORS.green, COLORS.orange, COLORS.pink];
const MEDIA_ESTADUAL = 5.7;

// ══════════════════════════════════════════════════
// DADOS MOCKADOS PARA DEMONSTRAÇÃO
// ══════════════════════════════════════════════════

const MOCK_STATS = {
  totals: { students: 4823, activeStudents: 4210, schools: 32, classes: 187 },
};

const MOCK_SUMMARY = {
  total_results: 12450,
  total_students_evaluated: 3890,
  total_simulados: 8,
  avg_score: 6.2,
  approval_rate: 64.3,
};

const MOCK_AVG_BY_SCHOOL = [
  { school: 'EM Prof. Maria Silva', avg: 7.1 },
  { school: 'EM João de Deus', avg: 6.8 },
  { school: 'EM Santa Clara', avg: 6.5 },
  { school: 'EM Dom Pedro II', avg: 6.3 },
  { school: 'EM Rui Barbosa', avg: 6.1 },
  { school: 'EM Castro Alves', avg: 5.9 },
  { school: 'EM Tiradentes', avg: 5.7 },
  { school: 'EM Machado de Assis', avg: 5.5 },
  { school: 'EM Monteiro Lobato', avg: 5.2 },
  { school: 'EM Santos Dumont', avg: 4.8 },
];

const MOCK_AVG_BY_GRADE = [
  { grade: '2o Ano', avg: 5.4 },
  { grade: '3o Ano', avg: 5.8 },
  { grade: '4o Ano', avg: 6.1 },
  { grade: '5o Ano', avg: 6.5 },
  { grade: '6o Ano', avg: 6.0 },
  { grade: '7o Ano', avg: 6.3 },
  { grade: '8o Ano', avg: 6.7 },
  { grade: '9o Ano', avg: 7.0 },
];

const MOCK_AVG_BY_CLASS = [
  { class_name: '5A - Matutino', school: 'EM Prof. Maria Silva', grade: '5o Ano', avg: 7.4, count: 28 },
  { class_name: '5B - Vespertino', school: 'EM João de Deus', grade: '5o Ano', avg: 7.1, count: 30 },
  { class_name: '9A - Matutino', school: 'EM Santa Clara', grade: '9o Ano', avg: 6.9, count: 32 },
  { class_name: '8A - Matutino', school: 'EM Dom Pedro II', grade: '8o Ano', avg: 6.7, count: 27 },
  { class_name: '7B - Vespertino', school: 'EM Rui Barbosa', grade: '7o Ano', avg: 6.5, count: 29 },
  { class_name: '4A - Matutino', school: 'EM Castro Alves', grade: '4o Ano', avg: 6.3, count: 25 },
  { class_name: '6A - Matutino', school: 'EM Tiradentes', grade: '6o Ano', avg: 6.1, count: 31 },
  { class_name: '3B - Vespertino', school: 'EM Machado de Assis', grade: '3o Ano', avg: 5.9, count: 26 },
  { class_name: '5C - Matutino', school: 'EM Monteiro Lobato', grade: '5o Ano', avg: 5.7, count: 28 },
  { class_name: '2A - Matutino', school: 'EM Santos Dumont', grade: '2o Ano', avg: 5.4, count: 24 },
  { class_name: '9B - Vespertino', school: 'EM Prof. Maria Silva', grade: '9o Ano', avg: 5.2, count: 30 },
  { class_name: '6B - Vespertino', school: 'EM João de Deus', grade: '6o Ano', avg: 5.0, count: 33 },
];

const MOCK_AVG_5ANO = [
  { school: 'EM Prof. Maria Silva', avg: 7.2 },
  { school: 'EM João de Deus', avg: 6.9 },
  { school: 'EM Santa Clara', avg: 6.5 },
  { school: 'EM Dom Pedro II', avg: 6.2 },
  { school: 'EM Rui Barbosa', avg: 5.9 },
  { school: 'EM Castro Alves', avg: 5.6 },
  { school: 'EM Tiradentes', avg: 5.3 },
  { school: 'EM Monteiro Lobato', avg: 5.0 },
];

const MOCK_DESCRIPTORS = [
  { code: 'D01', name: 'Localizar informações explícitas em um texto', rate: 72, min_rate: 45, max_rate: 92, q1: 58, q3: 82, median_rate: 70 },
  { code: 'D02', name: 'Estabelecer relações entre partes de um texto', rate: 65, min_rate: 38, max_rate: 88, q1: 52, q3: 76, median_rate: 64 },
  { code: 'D03', name: 'Inferir o sentido de uma palavra ou expressão', rate: 58, min_rate: 30, max_rate: 80, q1: 45, q3: 70, median_rate: 57 },
  { code: 'D04', name: 'Inferir uma informação implícita em um texto', rate: 54, min_rate: 28, max_rate: 78, q1: 40, q3: 65, median_rate: 53 },
  { code: 'D05', name: 'Interpretar texto com auxílio de material gráfico', rate: 61, min_rate: 35, max_rate: 85, q1: 48, q3: 73, median_rate: 60 },
  { code: 'D06', name: 'Identificar o tema de um texto', rate: 68, min_rate: 42, max_rate: 90, q1: 55, q3: 79, median_rate: 67 },
  { code: 'D07', name: 'Identificar o conflito gerador do enredo', rate: 45, min_rate: 22, max_rate: 72, q1: 33, q3: 58, median_rate: 44 },
  { code: 'D08', name: 'Estabelecer relação causa/consequência', rate: 52, min_rate: 25, max_rate: 75, q1: 38, q3: 64, median_rate: 51 },
  { code: 'D09', name: 'Diferenciar fato de opinião', rate: 48, min_rate: 20, max_rate: 70, q1: 35, q3: 60, median_rate: 47 },
  { code: 'D10', name: 'Identificar as marcas linguísticas', rate: 42, min_rate: 18, max_rate: 68, q1: 30, q3: 55, median_rate: 41 },
  { code: 'D11', name: 'Distinguir um fato da opinião sobre esse fato', rate: 55, min_rate: 30, max_rate: 78, q1: 42, q3: 67, median_rate: 54 },
  { code: 'D12', name: 'Identificar a finalidade de textos', rate: 70, min_rate: 48, max_rate: 90, q1: 58, q3: 80, median_rate: 69 },
  { code: 'D13', name: 'Reconhecer diferentes formas de tratar informação', rate: 63, min_rate: 40, max_rate: 84, q1: 50, q3: 74, median_rate: 62 },
  { code: 'D14', name: 'Identificar efeito de ironia ou humor', rate: 38, min_rate: 15, max_rate: 65, q1: 25, q3: 50, median_rate: 37 },
  { code: 'D15', name: 'Reconhecer posições distintas entre dois textos', rate: 50, min_rate: 28, max_rate: 74, q1: 38, q3: 62, median_rate: 49 },
];

const MOCK_EVOLUTION = [
  { period: 'Mar/2025', avg: 5.2, min: 3.1, max: 7.8 },
  { period: 'Abr/2025', avg: 5.5, min: 3.4, max: 8.0 },
  { period: 'Mai/2025', avg: 5.8, min: 3.6, max: 8.2 },
  { period: 'Jun/2025', avg: 6.0, min: 3.8, max: 8.3 },
  { period: 'Ago/2025', avg: 6.1, min: 3.9, max: 8.5 },
  { period: 'Set/2025', avg: 6.3, min: 4.1, max: 8.6 },
  { period: 'Out/2025', avg: 6.2, min: 4.0, max: 8.4 },
  { period: 'Nov/2025', avg: 6.5, min: 4.3, max: 8.8 },
];

const MOCK_DISTRIBUTION = [
  { range: '0-1', count: 12 },
  { range: '1-2', count: 45 },
  { range: '2-3', count: 120 },
  { range: '3-4', count: 310 },
  { range: '4-5', count: 520 },
  { range: '5-6', count: 780 },
  { range: '6-7', count: 890 },
  { range: '7-8', count: 650 },
  { range: '8-9', count: 380 },
  { range: '9-10', count: 183 },
];

const MOCK_IDEB_EVOLUTION = [
  { year: 2015, ideb: 4.2, meta: 4.5 },
  { year: 2017, ideb: 4.6, meta: 4.8 },
  { year: 2019, ideb: 5.0, meta: 5.1 },
  { year: 2021, ideb: 5.3, meta: 5.4 },
  { year: 2023, ideb: 5.6, meta: 5.7 },
  { year: 2025, ideb: 5.9, meta: 6.0 },
];

// ── Diário Digital ──
// O dia a dia da rede: aula registrada, chamada lançada e o que ficou para trás.
// Os números conversam entre si de propósito — previstas = lançadas + sem
// frequência + substituição + não realizadas + pendentes — para quem estiver
// vendo a demonstração poder conferir a conta.
const MOCK_DIARIO_RESUMO = {
  previstas: 4680,
  lancadas: 4012,
  sem_frequencia: 214,
  substituicoes: 156,
  nao_realizadas: 98,
  pendentes: 200,
  // cumpridos (completas + substituições) sobre esperadas (previstas menos as
  // não realizadas) — a mesma conta do Acompanhamento de Diários, para quem
  // conferir na demonstração fechar a soma.
  preenchimento: 91.0,
};

const MOCK_PREENCHIMENTO_POR_ESCOLA = [
  { school: 'EM Prof. Maria Silva', taxa: 97.4 },
  { school: 'EM João de Deus', taxa: 95.1 },
  { school: 'EM Santa Clara', taxa: 93.8 },
  { school: 'EM Dom Pedro II', taxa: 91.2 },
  { school: 'EM Rui Barbosa', taxa: 89.6 },
  { school: 'EM Castro Alves', taxa: 86.0 },
  { school: 'EM Tiradentes', taxa: 82.7 },
  { school: 'EM Machado de Assis', taxa: 78.3 },
  { school: 'EM Monteiro Lobato', taxa: 71.9 },
  { school: 'EM Santos Dumont', taxa: 64.5 },
];

const MOCK_DIARIO_SEMANA = [
  { dia: 'Seg', previstas: 195, lancadas: 181 },
  { dia: 'Ter', previstas: 195, lancadas: 186 },
  { dia: 'Qua', previstas: 195, lancadas: 174 },
  { dia: 'Qui', previstas: 195, lancadas: 188 },
  { dia: 'Sex', previstas: 195, lancadas: 169 },
];

// As cores são as mesmas do Acompanhamento de Diários, para quem vê a
// demonstração e depois abre o sistema reconhecer o quadro.
const MOCK_ESTADOS_AULA = [
  { chave: 'completa', rotulo: 'Completa', qtd: 4012, color: COLORS.green },
  { chave: 'sem_freq', rotulo: 'Sem frequência', qtd: 214, color: COLORS.yellow },
  { chave: 'substituicao', rotulo: 'Substituição', qtd: 156, color: COLORS.navy },
  { chave: 'nao_realizada', rotulo: 'Não realizada', qtd: 98, color: COLORS.blue },
  { chave: 'pendente', rotulo: 'Pendente', qtd: 200, color: COLORS.red },
];

// Uma linha só, com cada estado ocupando a sua fatia.
const MOCK_ESTADOS_EMPILHADOS = [
  MOCK_ESTADOS_AULA.reduce((linha, e) => ({ ...linha, [e.chave]: e.qtd }), { mes: 'Setembro' }),
];

const MOCK_TOP_SCHOOLS_IDEB = [
  { name: 'EM Prof. Maria Silva', ideb: 6.4 },
  { name: 'EM João de Deus', ideb: 6.1 },
  { name: 'EM Santa Clara', ideb: 5.9 },
  { name: 'EM Dom Pedro II', ideb: 5.7 },
  { name: 'EM Rui Barbosa', ideb: 5.5 },
];

// ══════════════════════════════════════════════════
// COMPONENTES (mesmos do Dashboard original)
// ══════════════════════════════════════════════════

function StatMiniCard({ title, value, subtitle, icon, color, darkMode }) {
  const colorMap = {
    blue: { bg: 'from-[#0974BA]/10 to-[#0974BA]/5', border: 'border-[#0974BA]/20', text: 'text-[#0974BA]' },
    green: { bg: 'from-[#13983D]/10 to-[#13983D]/5', border: 'border-[#13983D]/20', text: 'text-[#13983D]' },
    purple: { bg: 'from-[#2E4974]/10 to-[#2E4974]/5', border: 'border-[#2E4974]/20', text: 'text-[#2E4974]' },
    orange: { bg: 'from-[#F9B047]/10 to-[#F9B047]/5', border: 'border-[#F9B047]/20', text: 'text-[#F9B047]' },
    red: { bg: 'from-[#E83A3C]/10 to-[#E83A3C]/5', border: 'border-[#E83A3C]/20', text: 'text-[#E83A3C]' },
    teal: { bg: 'from-[#0974BA]/10 to-[#0974BA]/5', border: 'border-[#0974BA]/20', text: 'text-[#0974BA]' },
    pink: { bg: 'from-[#E83A3C]/10 to-[#E83A3C]/5', border: 'border-[#E83A3C]/20', text: 'text-[#E83A3C]' },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div className={`rounded-2xl p-5 border backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] ${
      darkMode
        ? `bg-gradient-to-br ${c.bg} ${c.border} border-opacity-30`
        : `bg-gradient-to-br ${c.bg} ${c.border} shadow-sm`
    }`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
          darkMode ? 'bg-slate-700/50 text-slate-400' : 'bg-slate-100 text-slate-500'
        }`}>{subtitle}</span>
      </div>
      <p className={`text-3xl font-bold ${c.text}`}>{value}</p>
      <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{title}</p>
    </div>
  );
}

function ChartCard({ title, children, darkMode, className = '', fullWidth = false }) {
  return (
    <div className={`rounded-2xl p-6 backdrop-blur-xl border transition-all duration-300 ${
      darkMode
        ? 'bg-slate-800/70 border-slate-700/10'
        : 'bg-white/90 border-slate-200 shadow-sm'
    } ${fullWidth ? 'col-span-full' : ''} ${className}`}>
      <h3 className={`text-base font-semibold mb-5 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function CustomTooltip({ active, payload, label, darkMode, suffix = '' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={`rounded-xl px-4 py-3 shadow-xl border text-sm ${
      darkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
    }`}>
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="text-xs">
          {p.name}: <span className="font-bold">{typeof p.value === 'number' ? p.value.toFixed(1) : p.value}{suffix}</span>
        </p>
      ))}
    </div>
  );
}

function DescriptorCandleChart({ data, darkMode }) {
  const chartData = data.slice(0, 20).map(d => ({
    code: d.code,
    name: d.name,
    rate: d.rate,
    min: d.min_rate,
    max: d.max_rate,
    q1: d.q1,
    q3: d.q3,
    median: d.median_rate,
  }));

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#334155' : '#e2e8f0'} />
        <XAxis dataKey="code" tick={{ fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
        <YAxis domain={[0, 100]} tick={{ fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 11 }}
          label={{ value: 'Taxa de Acertos (%)', angle: -90, position: 'insideLeft', style: { fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 11 } }}
        />
        <Tooltip content={({ active, payload }) => {
          if (!active || !payload?.length) return null;
          const d = payload[0]?.payload;
          if (!d) return null;
          return (
            <div className={`rounded-xl px-4 py-3 shadow-xl border text-sm max-w-xs ${
              darkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
            }`}>
              <p className="font-bold text-xs mb-1">{d.code}</p>
              <p className="text-xs opacity-80 mb-2">{d.name}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <span>Media:</span><span className="font-bold">{d.rate}%</span>
                <span>Mediana:</span><span className="font-bold">{d.median}%</span>
                <span>Min:</span><span>{d.min}%</span>
                <span>Max:</span><span>{d.max}%</span>
                <span>Q1:</span><span>{d.q1}%</span>
                <span>Q3:</span><span>{d.q3}%</span>
              </div>
            </div>
          );
        }} />
        <ReferenceLine y={60} stroke={COLORS.yellow} strokeDasharray="5 5" label={{ value: 'Meta 60%', fill: COLORS.yellow, fontSize: 10 }} />
        <Bar dataKey="rate" fill={COLORS.blue} radius={[4, 4, 0, 0]} barSize={20}>
          {chartData.map((entry, index) => (
            <Cell key={index} fill={entry.rate >= 60 ? COLORS.teal : entry.rate >= 40 ? COLORS.yellow : COLORS.red} />
          ))}
        </Bar>
        <Line type="monotone" dataKey="max" stroke={COLORS.green} strokeWidth={1} dot={{ r: 3 }} name="Max" />
        <Line type="monotone" dataKey="min" stroke={COLORS.red} strokeWidth={1} dot={{ r: 3 }} name="Min" />
        <Legend wrapperStyle={{ fontSize: 11, color: darkMode ? '#94a3b8' : '#64748b' }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ══════════════════════════════════════════════════
// COMPONENTE PRINCIPAL - DASHBOARD DEMO
// ══════════════════════════════════════════════════

function DashboardDemo() {
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();

  const stats = MOCK_STATS;
  const summary = MOCK_SUMMARY;
  const avgBySchool = MOCK_AVG_BY_SCHOOL;
  const avgByGrade = MOCK_AVG_BY_GRADE;
  const avgByClass = MOCK_AVG_BY_CLASS;
  const avg5ano = MOCK_AVG_5ANO;
  const descriptors = MOCK_DESCRIPTORS;
  const evolution = MOCK_EVOLUTION;
  const distribution = MOCK_DISTRIBUTION;
  const idebEvolution = MOCK_IDEB_EVOLUTION;
  const topSchoolsByIdeb = MOCK_TOP_SCHOOLS_IDEB;
  const diario = MOCK_DIARIO_RESUMO;
  const preenchimentoPorEscola = MOCK_PREENCHIMENTO_POR_ESCOLA;
  const diarioSemana = MOCK_DIARIO_SEMANA;
  const estadosDaAula = MOCK_ESTADOS_AULA;
  const estadosEmpilhados = MOCK_ESTADOS_EMPILHADOS;

  const descriptorLineData = useMemo(() => {
    return descriptors.slice(0, 15).map(d => ({
      code: d.code,
      rate: d.rate,
      name: d.name,
    }));
  }, [descriptors]);

  const axisColor = darkMode ? '#94a3b8' : '#64748b';
  const gridColor = darkMode ? '#334155' : '#e2e8f0';

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <header className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-5">
            <div>
              <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
                ? 'bg-gradient-to-r from-gray-50 to-gray-400'
                : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
              }`}>
                Dashboard de Simulados
                <span className="ml-3 text-lg font-medium" style={{
                  background: darkMode
                    ? 'linear-gradient(135deg, #0974BA, #13983D)'
                    : 'linear-gradient(135deg, #0974BA, #F9B047)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  - Cidade Exemplo
                </span>
              </h1>
              <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                Demonstracao com dados fictícios - Analise de desempenho nos simulados
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Voltar para Home */}
              <button
                onClick={() => navigate('/')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${darkMode
                  ? 'bg-slate-800 border border-slate-600 text-slate-300 hover:bg-slate-700'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 shadow-sm'
                }`}
              >
                ← Voltar para Home
              </button>

              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${darkMode
                  ? 'bg-slate-800 border border-slate-600 text-slate-300 hover:bg-slate-700'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 shadow-sm'
                }`}
              >
                {darkMode ? '☀️ Modo Claro' : '🌙 Modo Escuro'}
              </button>
            </div>
          </div>

          {/* Banner demo */}
          <div className={`rounded-2xl p-4 border ${
            darkMode ? 'bg-[#F9B047]/10 border-[#F9B047]/30' : 'bg-[#F9B047]/10 border-[#F9B047]/30'
          }`}>
            <p className={`text-sm font-medium ${darkMode ? 'text-[#F9B047]' : 'text-[#9a6a1e]'}`}>
              ⚠️ Esta é uma versão de demonstração com dados fictícios. Os gráficos e valores exibidos são apenas para ilustração do sistema SIDEB+.
            </p>
          </div>
        </header>

        {/* ── Cards de Resumo - Linha 1 ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <StatMiniCard title="Total de Alunos" value={stats.totals.students.toLocaleString('pt-BR')}
            subtitle={`${stats.totals.activeStudents.toLocaleString('pt-BR')} ativos`} icon="🎓" color="pink" darkMode={darkMode} />
          <StatMiniCard title="Alunos Avaliados" value={summary.total_students_evaluated.toLocaleString('pt-BR')}
            subtitle="Participantes" icon="👥" color="teal" darkMode={darkMode} />
          <StatMiniCard title="Escolas" value={stats.totals.schools}
            subtitle="Avaliadas" icon="🏫" color="orange" darkMode={darkMode} />
          <StatMiniCard title="Turmas" value={stats.totals.classes}
            subtitle="Cadastradas" icon="📚" color="green" darkMode={darkMode} />
        </div>

        {/* ── Cards de Resumo - Linha 2 ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <StatMiniCard title="Simulados Aplicados" value={summary.total_simulados}
            subtitle="Total" icon="📝" color="blue" darkMode={darkMode} />
          <StatMiniCard title="Nota Media Geral" value={summary.avg_score.toFixed(1)}
            subtitle="Escala 0-10" icon="📊" color="purple" darkMode={darkMode} />
          <StatMiniCard title="Taxa de Aprovacao" value={`${summary.approval_rate}%`}
            subtitle=">=60% aprovado" icon="✅" color="green" darkMode={darkMode} />
        </div>

        {/* ── Linha 1: Nota por Escola + Nota por Ano ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard title="Nota Media por Escola (com media estadual)" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={avgBySchool.slice(0, 12)} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="school" tick={{ fill: axisColor, fontSize: 10 }} angle={-35} textAnchor="end" height={80} interval={0}
                  tickFormatter={(v) => v.length > 18 ? v.substring(0, 18) + '...' : v} />
                <YAxis domain={[0, 10]} tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                <ReferenceLine y={MEDIA_ESTADUAL} stroke={COLORS.red} strokeDasharray="5 5" strokeWidth={2}
                  label={{ value: `Média Estadual (${MEDIA_ESTADUAL})`, fill: COLORS.red, fontSize: 10, position: 'top' }} />
                <Bar dataKey="avg" name="Media" radius={[6, 6, 0, 0]} barSize={30}>
                  {avgBySchool.slice(0, 12).map((entry, i) => (
                    <Cell key={i} fill={entry.avg >= MEDIA_ESTADUAL ? COLORS.teal : COLORS.red} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Nota Media por Ano/Serie (com media estadual)" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={avgByGrade} margin={{ top: 5, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="grade" tick={{ fill: axisColor, fontSize: 12 }} />
                <YAxis domain={[0, 10]} tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                <ReferenceLine y={MEDIA_ESTADUAL} stroke={COLORS.red} strokeDasharray="5 5" strokeWidth={2}
                  label={{ value: `Média Estadual (${MEDIA_ESTADUAL})`, fill: COLORS.red, fontSize: 10, position: 'top' }} />
                <Bar dataKey="avg" name="Media" radius={[6, 6, 0, 0]} barSize={40}>
                  {avgByGrade.map((entry, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* ── Linha 2: Nota por Turma + Media 5 ano por Escola ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard title="Nota Media por Turma" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={avgByClass.slice(0, 15)} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis type="number" domain={[0, 10]} tick={{ fill: axisColor, fontSize: 11 }} />
                <YAxis type="category" dataKey="class_name" width={120} tick={{ fill: axisColor, fontSize: 10 }}
                  tickFormatter={(v) => v.length > 18 ? v.substring(0, 18) + '...' : v} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <div className={`rounded-xl px-4 py-3 shadow-xl border text-sm ${
                      darkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
                    }`}>
                      <p className="font-bold">{d.class_name}</p>
                      <p className="text-xs opacity-70">{d.school} - {d.grade}</p>
                      <p className="mt-1">Media: <span className="font-bold">{d.avg.toFixed(1)}</span></p>
                      <p className="text-xs">{d.count} resultados</p>
                    </div>
                  );
                }} />
                <ReferenceLine x={MEDIA_ESTADUAL} stroke={COLORS.red} strokeDasharray="5 5" strokeWidth={2} />
                <Bar dataKey="avg" name="Media" radius={[0, 6, 6, 0]} barSize={16}>
                  {avgByClass.slice(0, 15).map((entry, i) => (
                    <Cell key={i} fill={entry.avg >= MEDIA_ESTADUAL ? COLORS.blue : COLORS.yellow} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Resultado Simulados - 5o Ano por Escola" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={avg5ano.slice(0, 10)} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="school" tick={{ fill: axisColor, fontSize: 10 }} angle={-35} textAnchor="end" height={80} interval={0}
                  tickFormatter={(v) => v.length > 18 ? v.substring(0, 18) + '...' : v} />
                <YAxis domain={[0, 10]} tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                <ReferenceLine y={MEDIA_ESTADUAL} stroke={COLORS.red} strokeDasharray="5 5" strokeWidth={2}
                  label={{ value: `Média Estadual (${MEDIA_ESTADUAL})`, fill: COLORS.red, fontSize: 10, position: 'top' }} />
                <Bar dataKey="avg" name="Media 5o Ano" radius={[6, 6, 0, 0]} barSize={30}>
                  {avg5ano.slice(0, 10).map((entry, i) => (
                    <Cell key={i} fill={entry.avg >= MEDIA_ESTADUAL ? COLORS.green : COLORS.red} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* ── Linha 3: Descritores x Taxa de Acertos (Candlestick) ── */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          <ChartCard title="Descritores x Taxa de Acertos (Min / Media / Max)" darkMode={darkMode} fullWidth>
            <DescriptorCandleChart data={descriptors} darkMode={darkMode} />
          </ChartCard>
        </div>

        {/* ── Linha 4: Grafico de Linha por Descritor + Evolucao Temporal ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard title="Taxa de Acertos por Descritor" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={descriptorLineData} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="code" tick={{ fill: axisColor, fontSize: 10 }} angle={-45} textAnchor="end" height={70} />
                <YAxis domain={[0, 100]} tick={{ fill: axisColor, fontSize: 11 }}
                  label={{ value: '%', angle: -90, position: 'insideLeft', style: { fill: axisColor } }} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <div className={`rounded-xl px-4 py-3 shadow-xl border text-sm ${
                      darkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
                    }`}>
                      <p className="font-bold text-xs">{d.code}</p>
                      <p className="text-xs opacity-70 mb-1">{d.name}</p>
                      <p>Acertos: <span className="font-bold">{d.rate}%</span></p>
                    </div>
                  );
                }} />
                <ReferenceLine y={60} stroke={COLORS.yellow} strokeDasharray="5 5" label={{ value: 'Meta 60%', fill: COLORS.yellow, fontSize: 10 }} />
                <Line type="monotone" dataKey="rate" stroke={COLORS.blue} strokeWidth={2.5} dot={{ r: 4, fill: COLORS.blue }}
                  activeDot={{ r: 6, fill: COLORS.blue }} name="Taxa de Acertos" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Evolucao da Media ao Longo do Tempo" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={evolution} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="period" tick={{ fill: axisColor, fontSize: 11 }} />
                <YAxis domain={[0, 10]} tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                <ReferenceLine y={MEDIA_ESTADUAL} stroke={COLORS.red} strokeDasharray="5 5"
                  label={{ value: 'Média Estadual', fill: COLORS.red, fontSize: 10 }} />
                <Area type="monotone" dataKey="avg" fill={COLORS.blue} fillOpacity={0.1} stroke="none" />
                <Line type="monotone" dataKey="avg" stroke={COLORS.blue} strokeWidth={3} dot={{ r: 5, fill: COLORS.blue }}
                  activeDot={{ r: 7 }} name="Media" />
                <Line type="monotone" dataKey="max" stroke={COLORS.teal} strokeWidth={1} strokeDasharray="3 3" dot={false} name="Max" />
                <Line type="monotone" dataKey="min" stroke={COLORS.red} strokeWidth={1} strokeDasharray="3 3" dot={false} name="Min" />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* ── Linha 5: Distribuicao de Notas + IDEB Evolution ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard title="Distribuicao de Notas dos Simulados" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={distribution} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="range" tick={{ fill: axisColor, fontSize: 11 }} />
                <YAxis tick={{ fill: axisColor, fontSize: 11 }}
                  label={{ value: 'Qtd Alunos', angle: -90, position: 'insideLeft', style: { fill: axisColor, fontSize: 11 } }} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} suffix=" alunos" />} />
                <Bar dataKey="count" name="Alunos" radius={[4, 4, 0, 0]}>
                  {distribution.map((entry, i) => {
                    const val = parseInt(entry.range);
                    let color = COLORS.red;
                    if (val >= 6) color = COLORS.teal;
                    else if (val >= 4) color = COLORS.yellow;
                    return <Cell key={i} fill={color} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Evolucao do IDEB Municipal" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={idebEvolution} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="year" tick={{ fill: axisColor, fontSize: 11 }} />
                <YAxis domain={[3, 7]} tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                <Line type="monotone" dataKey="ideb" stroke={COLORS.blue} strokeWidth={3}
                  dot={{ r: 5, fill: COLORS.blue }} name="IDEB Municipal" />
                <Line type="monotone" dataKey="meta" stroke={COLORS.yellow} strokeWidth={2}
                  strokeDasharray="5 5" dot={false} name="Meta" />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* ── Linha 6: Top Escolas IDEB ── */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          <ChartCard title="Top 5 Escolas - IDEB" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={topSchoolsByIdeb} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis type="number" domain={[0, 7]} tick={{ fill: axisColor, fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={160} tick={{ fill: axisColor, fontSize: 10 }}
                  tickFormatter={(v) => v.length > 25 ? v.substring(0, 25) + '...' : v} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
                <Bar dataKey="ideb" name="IDEB" radius={[0, 6, 6, 0]} barSize={20}>
                  {topSchoolsByIdeb.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* ══ Diário Digital ══
            O outro lado do sistema: não o resultado da avaliação, mas o dia a
            dia que o alimenta. Fica por último porque é a parte nova. */}
        <div className="mt-12 mb-6">
          <h2 className={`text-xl font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
            Diário Digital
          </h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Aula por aula: o que a grade previa, o que os professores lançaram e o que ficou pendente.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatMiniCard title="Preenchimento do mês" value={`${diario.preenchimento}%`}
            subtitle="Aulas futuras não contam" icon="📗" color="green" darkMode={darkMode} />
          <StatMiniCard title="Aulas previstas" value={diario.previstas.toLocaleString('pt-BR')}
            subtitle="Pela grade horária" icon="🕘" color="blue" darkMode={darkMode} />
          <StatMiniCard title="Sem frequência" value={diario.sem_frequencia}
            subtitle="Aula registrada, falta a chamada" icon="📋" color="orange" darkMode={darkMode} />
          <StatMiniCard title="Pendentes" value={diario.pendentes}
            subtitle="Nenhum registro na aula" icon="⏳" color="red" darkMode={darkMode} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard title="Preenchimento do diário por escola" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={preenchimentoPorEscola} layout="vertical"
                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis type="number" domain={[0, 100]} tick={{ fill: axisColor, fontSize: 11 }} />
                <YAxis type="category" dataKey="school" width={150} tick={{ fill: axisColor, fontSize: 10 }}
                  tickFormatter={(v) => v.length > 22 ? v.substring(0, 22) + '...' : v} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} suffix="%" />} />
                <Bar dataKey="taxa" name="Preenchimento" radius={[0, 6, 6, 0]} barSize={16}>
                  {preenchimentoPorEscola.map((e, i) => {
                    let color = COLORS.red;
                    if (e.taxa >= 90) color = COLORS.green;
                    else if (e.taxa >= 80) color = COLORS.yellow;
                    return <Cell key={i} fill={color} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Aulas previstas x lançadas na semana" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={diarioSemana} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis dataKey="dia" tick={{ fill: axisColor, fontSize: 11 }} />
                <YAxis tick={{ fill: axisColor, fontSize: 11 }} />
                <Tooltip content={<CustomTooltip darkMode={darkMode} suffix=" aulas" />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="previstas" name="Previstas" fill={COLORS.navy} radius={[4, 4, 0, 0]} />
                <Bar dataKey="lancadas" name="Lançadas" fill={COLORS.green} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 gap-6 mb-6">
          <ChartCard title="Situação das 4.680 aulas previstas no mês" darkMode={darkMode}>
            <ResponsiveContainer width="100%" height={110}>
              <BarChart data={estadosEmpilhados} layout="vertical"
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <XAxis type="number" hide domain={[0, 4680]} />
                <YAxis type="category" dataKey="mes" hide />
                <Tooltip content={<CustomTooltip darkMode={darkMode} suffix=" aulas" />} />
                {/* Sem <Legend>: a linha de números abaixo já nomeia cada cor,
                    na ordem da barra — o Recharts reordena a sua. */}
                {estadosDaAula.map((e, i) => (
                  <Bar key={e.chave} dataKey={e.chave} name={e.rotulo} stackId="mes"
                    fill={e.color} barSize={44}
                    radius={i === 0 ? [6, 0, 0, 6]
                      : i === estadosDaAula.length - 1 ? [0, 6, 6, 0] : 0} />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3">
              {estadosDaAula.map((e) => (
                <span key={e.chave} className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <strong style={{ color: e.color }}>{e.qtd.toLocaleString('pt-BR')}</strong> {e.rotulo}
                </span>
              ))}
            </div>
            <p className={`text-xs mt-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <strong>Substituição</strong>: o professor faltou e um colega cobriu a aula. O horário
              conta como cumprido para quem faltou, e a aula fica registrada no nome de quem deu —
              em vez de ficar pendente dos dois lados.
            </p>
          </ChartCard>
        </div>

      </div>
    </div>
  );
}

export default DashboardDemo;
